import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { RadioTower, Copy, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";

export const TruthPage = () => {
  const { brandSlug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/truth/${brandSlug}`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true));
  }, [brandSlug]);

  const towerUrl = `${process.env.REACT_APP_BACKEND_URL}/truth/${brandSlug}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(towerUrl);
      setCopied(true);
      toast.success("Broadcast Tower URL copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Select and copy the URL manually.");
    }
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center" data-testid="truth-page-not-found">
        <p className="text-slate-400">No Source-of-Truth page found for this brand.</p>
      </div>
    );
  }

  if (!data) {
    return <div className="mx-auto max-w-2xl px-6 py-24 text-center text-slate-500" data-testid="truth-page-loading">Loading Broadcast Tower...</div>;
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-16" data-testid="truth-page">
      <div className="mb-2 flex items-center gap-2">
        <RadioTower className="h-6 w-6 text-sovereign" />
        <h1 className="font-heading text-3xl font-bold text-white">{data.brand_name}</h1>
      </div>
      <p className="mb-6 text-slate-400">
        Verified Facts &amp; Truth Statements - BrandSignal AI Visibility Score: {data.visibility_score ?? "N/A"}/100
      </p>

      <div className="mb-8 rounded-lg border border-sovereign/30 bg-sovereign/5 p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Your Broadcast Tower URL</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded bg-obsidian px-3 py-2 text-sm text-openai-cyan" data-testid="truth-tower-url">{towerUrl}</code>
          <Button size="sm" variant="outline" onClick={copyUrl} className="gap-2 border-surface-border text-slate-200 hover:text-white" data-testid="truth-tower-copy-btn">
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-500">Submit this URL as a backlink or in directories so AI crawlers ingest these corrected facts.</p>
      </div>

      {data.truth_statements.length === 0 ? (
        <p className="text-slate-500" data-testid="truth-page-empty">No disputed claims on file for this brand.</p>
      ) : (
        <div className="space-y-4" data-testid="truth-statements-list">
          {data.truth_statements.map((s, i) => (
            <article key={i} className="rounded-lg border-l-4 border-sovereign bg-surface-card p-4" data-testid={`truth-statement-${i}`}>
              <p className="mb-1 text-sm text-slate-500">Claim: {s.claim}</p>
              <p className="text-sm font-semibold text-truth-green">Verified Fact: {s.truth_statement}</p>
              {s.source_audit_date && (
                <p className="mt-2 text-xs uppercase tracking-wide text-slate-600" data-testid={`truth-statement-date-${i}`}>
                  Verified on {new Date(s.source_audit_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                </p>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
